"use client";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Shield,
  CreditCard,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  DollarSign,
  Layers,
  Hourglass,
  History,
  Calendar,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { Link } from "react-router-dom"; // No need for useNavigate here anymore
import api from "../services/api"; // Ensure this imports the configured axios instance
import toast from "react-hot-toast";
import type { Plan } from "../types/Plan";

interface PlanDetails {
  id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  formattedAmount: string;
  formattedDuration: string;
  startDate: string;
  endDate: string;
  status: string;
  isActive: boolean;
}

interface UserProfile {
  _id: string;
  company_name: string;
  email: string;
  mobile: string;
  customer_type: string;
  gstNo: string;
  tally_serialno: string;
  tally_version: string;
  category?: string;
  created_at: string;
  plan?: PlanDetails;
}

interface Consent {
  _id: string;
  partnerName: string;
  consentId: string;
  consentStatus: "Pending" | "Approved" | "Rejected" | "Expired";
  lastUpdated: string;
}

interface Order {
  _id: string;
  planId: {
    _id: string;
    planName: string;
  };
  amount: number;
  formattedAmount: string;
  orderDate: string;
  paymentStatus: "Pending" | "Completed" | "Failed" | "Refunded";
  receiptUrl?: string;
}

interface ActivityItem {
  id: string;
  type: "consent" | "order" | "profile";
  title: string;
  description: string;
  date: string;
  status: "success" | "pending" | "failed";
}

interface DashboardStats {
  totalConsents: number;
  activeConsents: number;
  pendingConsents: number;
  expiredConsents: number;
  totalPlans: number;
  totalOrders: number;
  completedOrders: number;
  pendingOrders: number;
  failedOrders: number;
  recentActivity: ActivityItem[];
}

export default function DashboardPage() {
  const { user: authUser } = useAuthStore(); // `logout` is no longer needed here, handled by interceptor

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalConsents: 0,
    activeConsents: 0,
    pendingConsents: 0,
    expiredConsents: 0,
    totalPlans: 0,
    totalOrders: 0,
    completedOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    recentActivity: [],
  });
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch User Profile (includes current plan)
      const userProfileResponse = await api.get("/auth/profile");
      if (userProfileResponse.data.user) {
        setUserProfile(userProfileResponse.data.user);
      }

      if (userProfileResponse.data.message === "Not authorized") {
        await api.get("/auth/profile");
      }
      // Fetch Consents
      const consentsResponse = await api.get(
        `/user-consents/user/${authUser?.id}`
      );
      const consents: Consent[] = consentsResponse.data.success
        ? consentsResponse.data.data
        : [];

      // Fetch Orders
      const ordersResponse = await api.get("/orders/history");
      const orders: Order[] = ordersResponse.data.success
        ? ordersResponse.data.orders
        : [];

      // Fetch All Plans
      const plansResponse = await api.get("/plans");
      const plansData: Plan[] = plansResponse.data || [];

      setPlans(plansData);

      // Calculate Consent Stats
      const totalConsents = consents.length;
      const activeConsents = consents.filter(
        (c) => c.consentStatus === "Approved"
      ).length;
      const pendingConsents = consents.filter(
        (c) => c.consentStatus === "Pending"
      ).length;
      const expiredConsents = consents.filter(
        (c) => c.consentStatus === "Expired"
      ).length;

      // Calculate Order Stats
      const totalOrders = orders.length;
      const completedOrders = orders.filter(
        (o) => o.paymentStatus === "Completed"
      ).length;
      const pendingOrders = orders.filter(
        (o) => o.paymentStatus === "Pending"
      ).length;
      const failedOrders = orders.filter(
        (o) => o.paymentStatus === "Failed"
      ).length;

      // Prepare Recent Activity
      const recentConsentsActivity: ActivityItem[] = consents
        .slice(0, 2)
        .map((consent) => ({
          id: consent._id,
          type: "consent" as const,
          title: `Consent ${consent.consentStatus}`,
          description: `${consent.partnerName} - ${consent.consentId}`,
          date: consent.lastUpdated,
          status:
            consent.consentStatus === "Approved"
              ? ("success" as const)
              : consent.consentStatus === "Pending"
              ? ("pending" as const)
              : ("failed" as const),
        }));
      const recentOrdersActivity: ActivityItem[] = orders
        .slice(0, 2)
        .map((order) => ({
          id: order._id,
          type: "order" as const,
          title: `Order ${order.paymentStatus}`,
          description: `${order.planId?.planName || "N/A"} - ${
            order.formattedAmount
          }`,
          date: order.orderDate,
          status:
            order.paymentStatus === "Completed"
              ? ("success" as const)
              : order.paymentStatus === "Pending"
              ? ("pending" as const)
              : ("failed" as const),
        }));

      const combinedRecentActivity: ActivityItem[] = [
        ...recentConsentsActivity,
        ...recentOrdersActivity,
        {
          id: "profile-welcome",
          type: "profile" as const,
          title: "Account Active",
          description: `Welcome ${
            userProfileResponse.data.user?.company_name || "User"
          }`,
          date: new Date().toISOString(),
          status: "success" as const,
        },
      ]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 4); // Get top 4 most recent activities

      setStats({
        totalConsents,
        activeConsents,
        pendingConsents,
        expiredConsents,
        totalPlans: plansData.length,
        totalOrders,
        completedOrders,
        pendingOrders,
        failedOrders,
        recentActivity: combinedRecentActivity,
      });
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      // The global Axios interceptor will now handle 200 (JWT expired) errors.
      // This catch block will only be reached for other types of network or API errors.
      toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: "Total Consents",
      value: stats.totalConsents.toString(),
      change: `${stats.activeConsents} Active`,
      trend: stats.activeConsents > 0 ? "up" : "neutral",
      icon: Shield,
      color: "bg-blue-500",
      link: "/consents",
    },
    {
      title: "Active Consents",
      value: stats.activeConsents.toString(),
      change: `${stats.pendingConsents} Pending`,
      trend: stats.activeConsents > stats.pendingConsents ? "up" : "down",
      icon: CheckCircle,
      color: "bg-green-500",
      link: "/consents",
    },
    {
      title: "Total Orders",
      value: stats.totalOrders.toString(),
      change: `${stats.completedOrders} Completed`,
      trend: stats.completedOrders > 0 ? "up" : "neutral",
      icon: DollarSign,
      color: "bg-indigo-500",
      link: "/orders", // Link to the new orders page
    },
    {
      title: "Available Plans",
      value: stats.totalPlans ? stats.totalPlans.toString() : "N/A",
      change: "View All",
      trend: "up",
      icon: CreditCard,
      color: "bg-purple-500",
      link: "/plans",
    },
  ];

  const formatIndianDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN"); // DD/MM/YYYY
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {userProfile?.company_name || "User"}!
        </h1>
        <p className="text-gray-600">
          Here's an overview of your account and recent activity.
        </p>
      </motion.div>

      {/* Account Overview Section (Moved and Enhanced) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-xl shadow-lg p-6 text-white relative overflow-hidden"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-3">Account Overview</h2>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-blue-100 text-lg">
              <div className="flex flex-col">
                <p className="text-sm opacity-80">Company</p>
                <p className="font-semibold text-white">
                  {userProfile?.company_name || "N/A"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm opacity-80">Customer Type</p>
                <p className="font-semibold text-white">
                  {userProfile?.customer_type || "N/A"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm opacity-80">GST Number</p>
                <p className="font-semibold text-white">
                  {userProfile?.gstNo || "N/A"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm opacity-80">Tally Version</p>
                <p className="font-semibold text-white">
                  {userProfile?.tally_version || "N/A"}
                </p>
              </div>
            </div>
          </div>
          <div className="hidden md:block flex-shrink-0">
            <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
            >
              <Link to={card.link} className="block">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`${card.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div
                      className={`flex items-center text-sm ${
                        card.trend === "up"
                          ? "text-green-600"
                          : card.trend === "down"
                          ? "text-red-600"
                          : "text-gray-600"
                      }`}
                    >
                      {card.trend === "up" && (
                        <ArrowUpRight size={16} className="mr-1" />
                      )}
                      {card.trend === "down" && (
                        <ArrowDownRight size={16} className="mr-1" />
                      )}
                      {card.change}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {card.value}
                    </h3>
                    <p className="text-gray-600 text-sm">{card.title}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Main Content Grid: Your Current Plan & Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Your Current Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Layers size={24} className="mr-2 text-purple-600" />
            Your Current Plan
          </h2>
          {userProfile?.plan ? (
            <div className="space-y-4">
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <CreditCard size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Plan Name</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {userProfile.plan.planName}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <DollarSign size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {userProfile.plan.formattedAmount}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Hourglass size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">Duration</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {userProfile.plan.formattedDuration}
                  </p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Calendar size={20} className="text-purple-600" />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Active Until
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatIndianDate(userProfile.plan.endDate)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No active plan found.</p>
              <p className="text-gray-400 text-sm">
                Contact support to subscribe to a plan.
              </p>
              <Link
                to="/plans"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Browse Plans
              </Link>
            </div>
          )}
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Recent Orders
              </h2>
              <Link
                to="/orders"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {stats.recentActivity.filter((item) => item.type === "order")
              .length > 0 ? (
              stats.recentActivity
                .filter((item) => item.type === "order")
                .map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                            activity.status === "success"
                              ? "bg-green-100"
                              : activity.status === "pending"
                              ? "bg-yellow-100"
                              : "bg-red-100"
                          }`}
                        >
                          {activity.status === "success" && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                          {activity.status === "pending" && (
                            <Clock className="w-5 h-5 text-yellow-600" />
                          )}
                          {activity.status === "failed" && (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activity.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {formatIndianDate(activity.date)}
                        </p>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            activity.status === "success"
                              ? "bg-green-100 text-green-800"
                              : activity.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {activity.status}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))
            ) : (
              <div className="p-6 text-center text-gray-500">
                <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No recent orders</p>
                <Link
                  to="/orders"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Browse Orders
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Recent Activity (Combined) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              All Recent Activity
            </h2>
            <Link
              to="/activity" // Assuming a dedicated activity log page
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All
            </Link>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {stats.recentActivity.length > 0 ? (
            stats.recentActivity.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.7 + index * 0.05 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                        activity.status === "success"
                          ? "bg-green-100"
                          : activity.status === "pending"
                          ? "bg-yellow-100"
                          : "bg-red-100"
                      }`}
                    >
                      {activity.status === "success" && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      {activity.status === "pending" && (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}
                      {activity.status === "failed" && (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {activity.title}
                      </p>
                      <p className="text-sm text-gray-600">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {formatIndianDate(activity.date)}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        activity.status === "success"
                          ? "bg-green-100 text-green-800"
                          : activity.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {activity.status}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No recent activity to display.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/consents"
            className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">View Consents</p>
              <p className="text-sm text-gray-600">Manage API permissions</p>
            </div>
          </Link>
          <Link
            to="/plans"
            className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-purple-200 transition-colors">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Browse Plans</p>
              <p className="text-sm text-gray-600">Explore available options</p>
            </div>
          </Link>
          <Link
            to="/profile"
            className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
              <User className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Update Profile</p>
              <p className="text-sm text-gray-600">Manage account details</p>
            </div>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
